"use client";

import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/components/providers/auth-provider";
import { formatDate } from "@/lib/format-datetime";
import type { Locale } from "@/lib/i18n";
import { bandMessageKey, fetchSkillTestStatus } from "./api";
import { signInHref } from "@/lib/post-login-redirect";

/**
 * The Skill Test card in the track page's aside (`LN-TST01`, docs/35 §4 and §6).
 *
 * Closed track: the scale and "not open yet", as before. Open track: one
 * action that says exactly what pressing it does (the free attempt, a bought
 * one, resume, or the result being graded), the reason a start would be refused
 * when it would be, the viewer's best band, and the readiness check. Every
 * action leads to the intro or the result page; nothing here spends an attempt.
 *
 * `LN-CRT01`: a learner out of attempts is sent to the track's certificate
 * page, where the buying happens (docs/35 §7), and a held certificate is named
 * with a link to its verify page.
 */
export function SkillTestCard({
  trackSlug,
  open,
  locale,
}: {
  trackSlug: string;
  open: boolean;
  locale: Locale;
}) {
  const t = useTranslations("learn");
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { data: status, isLoading, isError } = useQuery({
    queryKey: ["skill-test", trackSlug, "status", isAuthenticated],
    queryFn: () => fetchSkillTestStatus(trackSlug),
    enabled: open && !authLoading,
    staleTime: 30_000,
  });

  const base = `/${locale}/learn/${trackSlug}`;
  const viewer = status?.viewer ?? null;
  const bestKey = bandMessageKey(viewer?.best?.skillBand);

  let action: { href: string; label: string } | null = null;
  if (open && status) {
    if (!isAuthenticated) {
      action = {
        href: signInHref(locale, `${base}/skill-test`),
        label: t("skillTest.signIn"),
      };
    } else if (viewer?.openAttemptId) {
      action = viewer.openAttemptSitting
        ? { href: `${base}/skill-test/attempt`, label: t("skillTest.resume") }
        : { href: `${base}/skill-test/result/${viewer.openAttemptId}`, label: t("skillTest.viewResult") };
    } else if (viewer?.canStart) {
      action = {
        href: `${base}/skill-test`,
        label: viewer.freeAvailable && viewer.emailVerified ? t("skillTest.startFree") : t("skillTest.start"),
      };
    } else if (viewer?.blockedReason === "no_credit" && status.offer?.onSale) {
      action = { href: `${base}/certificate`, label: t("skillTest.buyCertificate") };
    }
  }
  const heldCertificate = viewer?.certificate ?? null;
  const heldKey = bandMessageKey(heldCertificate?.skillBand);

  let note: string | null = null;
  if (viewer && !viewer.openAttemptId) {
    if (viewer.blockedReason === "gap" && viewer.nextEligibleAt) {
      note = t("skillTest.gap", { date: formatDate(viewer.nextEligibleAt, locale) });
    } else if (viewer.blockedReason === "email_not_verified") {
      note = t("skillTest.verifyEmail");
    } else if (viewer.blockedReason === "no_credit") {
      note = t("skillTest.noCredit");
    } else if (viewer.freeAvailable && viewer.emailVerified) {
      note = t("skillTest.freeLine");
    } else if (viewer.purchasedRemaining > 0) {
      note = t("skillTest.purchased", { count: viewer.purchasedRemaining });
    } else if (viewer.proCreditAvailable) {
      note = t("skillTest.proCredit");
    }
  } else if (open && status && !isAuthenticated) {
    note = t("skillTest.freeLine");
  }

  return (
    <Card className="mt-4 p-5">
      <h2 className="font-[family-name:var(--font-heading)] text-base font-semibold text-[rgb(var(--foreground))]">
        {t("skillTestHeading")}
      </h2>
      {!open ? (
        <>
          <p className="mt-2 text-xs leading-relaxed text-[rgb(var(--muted-foreground))]">
            {t("bandsScale")}
          </p>
          <p className="mt-3 text-xs font-medium text-[rgb(var(--foreground))]">
            {t("certificatesNotOpen")}
          </p>
        </>
      ) : isError ? (
        <p className="mt-3 text-xs text-[rgb(var(--muted-foreground))]">{t("readiness.errorGeneric")}</p>
      ) : isLoading || !status ? (
        <div className="mt-3 space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : (
        <>
          <p className="mt-2 text-xs leading-relaxed text-[rgb(var(--muted-foreground))]">
            {t("skillTest.cardOpenBody")}
          </p>
          {viewer?.best && bestKey ? (
            <p className="mt-3 text-sm font-medium text-[rgb(var(--foreground))]">
              <Link
                className="hover:text-primary hover:underline"
                href={`${base}/skill-test/result/${viewer.best.attemptId}`}
              >
                {t("skillTest.best", { score: viewer.best.skillScore, band: t(bestKey) })}
              </Link>
            </p>
          ) : null}
          {note ? <p className="mt-3 text-xs text-[rgb(var(--foreground))]">{note}</p> : null}
          {heldCertificate && heldKey ? (
            <p className="mt-3 text-xs text-[rgb(var(--foreground))]">
              {t("skillTest.certificateHeld", { score: heldCertificate.skillScore, band: t(heldKey) })}{" "}
              <Link
                className="font-medium text-primary-ink hover:underline"
                href={`/${locale}/verify/${heldCertificate.certificateId}`}
              >
                {t("skillTest.viewCertificate")}
              </Link>
            </p>
          ) : viewer?.issuePending ? (
            <p className="mt-3 text-xs text-[rgb(var(--foreground))]">{t("skillTest.certificatePending")}</p>
          ) : null}
          {action ? (
            <Button asChild className="mt-4 w-full">
              <Link href={action.href}>{action.label}</Link>
            </Button>
          ) : null}
          <Link
            className="mt-3 inline-flex text-xs text-[rgb(var(--muted-foreground))] hover:text-primary hover:underline"
            href={`${base}/readiness`}
          >
            {t("skillTest.readinessLink")}
          </Link>
        </>
      )}
    </Card>
  );
}
